import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Navigation } from '@/components/Navigation';
import { BackButton } from '@/components/BackButton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Trophy, Star, Heart, Edit2, Award, Video, BarChart3, Sparkles, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { profileSchema } from '@/lib/validationSchemas';
import { NotificationPreferences } from '@/components/settings/NotificationPreferences';
import { DataExport } from '@/components/settings/DataExport';
import { FollowersList } from '@/components/profile/FollowersList';
import { FollowingList } from '@/components/profile/FollowingList';
import { SuggestedCreators } from '@/components/profile/SuggestedCreators';
import { useFollow } from '@/hooks/useFollow';
import { ReferralDashboard } from '@/components/referral/ReferralDashboard';
import { ApplyReferralCode } from '@/components/referral/ApplyReferralCode';

const Profile = () => {
  const { user, userRole } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any>(null);
  const [badges, setBadges] = useState<any[]>([]);
  const [recordings, setRecordings] = useState<any[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const [loading, setLoading] = useState(true);
  
  const { fansCount, followingCount } = useFollow(user?.id || null);
  const isCreator = userRole === 'creator';

  useEffect(() => {
    loadProfile();
    loadBadges();
    if (isCreator) {
      loadRecordings();
    }
  }, [isCreator]);

  const loadProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();
      
      if (error) {
        console.error('Error loading profile:', error);
        toast.error('Failed to load profile');
      } else if (data) {
        setProfile(data);
        setDisplayName(data.display_name || '');
        setBio(data.bio || '');
      } else {
        // Profile doesn't exist - set a default profile object
        setProfile({
          id: user.id,
          username: user.email?.split('@')[0] || 'user',
          display_name: user.email?.split('@')[0] || 'User',
          bio: '',
          avatar_url: null
        });
      }
    } catch (error) {
      console.error('Error in loadProfile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const loadBadges = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data } = await supabase
      .from('user_badges')
      .select('*, badges(*)')
      .eq('user_id', user.id);
    
    if (data) setBadges(data);
  };

  const loadRecordings = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data } = await supabase
      .from('recordings')
      .select('*')
      .eq('creator_id', user.id)
      .eq('is_published', true)
      .eq('status', 'completed')
      .order('published_at', { ascending: false })
      .limit(20);

    if (data) setRecordings(data);
  };

  const saveProfile = async () => {
    // Validate profile data
    const validation = profileSchema.safeParse({
      displayName,
      bio
    });

    if (!validation.success) {
      toast.error(validation.error.errors[0].message);
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from('profiles')
      .update({
        display_name: validation.data.displayName,
        bio: validation.data.bio
      })
      .eq('id', user.id);

    if (error) {
      toast.error('Failed to update profile');
    } else {
      toast.success('Profile updated!');
      setIsEditing(false);
      loadProfile();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Navigation />
        <div className="container py-8 max-w-4xl flex-1">
          <Card className="mb-6 clean-card">
            <CardContent className="p-6">
              <p className="text-center text-muted-foreground">Loading profile...</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Navigation />
        <div className="container py-8 max-w-4xl flex-1">
          <Card className="mb-6 clean-card">
            <CardContent className="p-6">
              <p className="text-center text-muted-foreground">Profile not found. Please sign in.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navigation />
      
      <div className="container py-8 max-w-4xl flex-1">
        <BackButton to="/" />
        {/* Creator Quick Actions */}
        {isCreator && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="clean-card hover-lift cursor-pointer" onClick={() => navigate('/go-live')}>
              <CardContent className="p-6 text-center">
                <div className="flex flex-col items-center gap-2">
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Video className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-semibold">Go Live</h3>
                  <p className="text-xs text-muted-foreground">Start streaming now</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="clean-card hover-lift cursor-pointer" onClick={() => navigate('/creator-dashboard')}>
              <CardContent className="p-6 text-center">
                <div className="flex flex-col items-center gap-2">
                  <div className="h-12 w-12 rounded-full bg-secondary/10 flex items-center justify-center">
                    <BarChart3 className="h-6 w-6 text-secondary" />
                  </div>
                  <h3 className="font-semibold">Dashboard</h3>
                  <p className="text-xs text-muted-foreground">View analytics & earnings</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="clean-card hover-lift cursor-pointer">
              <CardContent className="p-6 text-center">
                <div className="flex flex-col items-center gap-2">
                  <div className="h-12 w-12 rounded-full bg-accent/10 flex items-center justify-center">
                    <Sparkles className="h-6 w-6 text-accent" />
                  </div>
                  <h3 className="font-semibold">Manage Content</h3>
                  <p className="text-xs text-muted-foreground">Clips & memberships</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Profile Header */}
        <Card className="mb-6 clean-card">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-6 items-start">
              <Avatar className="h-24 w-24 border-2 border-primary/10">
                <AvatarImage src={profile.avatar_url} />
                <AvatarFallback className="text-3xl bg-primary/10 text-primary">
                  {profile.display_name?.[0] || 'U'}
                </AvatarFallback>
              </Avatar>

              <div className="flex-1">
                {isEditing ? (
                  <div className="space-y-3">
                    <Input
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      placeholder="Display Name"
                      className="rounded-xl"
                    />
                    <Textarea
                      value={bio}
                      onChange={(e) => setBio(e.target.value)}
                      placeholder="Tell us about yourself..."
                      rows={3}
                      className="rounded-xl"
                    />
                    <div className="flex gap-2">
                      <Button onClick={saveProfile} className="rounded-full">Save</Button>
                      <Button 
                        variant="outline" 
                        onClick={() => setIsEditing(false)}
                        className="rounded-full"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                          {profile.display_name}
                          {isCreator && (
                            <Badge variant="default" className="rounded-full">Creator</Badge>
                          )}
                        </h1>
                        <p className="text-sm text-muted-foreground">
                          @{profile.username}
                        </p>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => setIsEditing(true)}
                        className="rounded-full"
                      >
                        <Edit2 className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                    </div>
                    {profile.bio && (
                      <p className="text-muted-foreground mb-3">{profile.bio}</p>
                    )}
                    <div className="flex gap-4">
                      <div>
                        <span className="font-bold text-foreground">{fansCount}</span>
                        <span className="text-sm text-muted-foreground ml-1">Fans</span>
                      </div>
                      <div>
                        <span className="font-bold text-foreground">{followingCount}</span>
                        <span className="text-sm text-muted-foreground ml-1">Following</span>
                      </div>
                      <div>
                        <span className="font-bold text-foreground">{badges.length}</span>
                        <span className="text-sm text-muted-foreground ml-1">Badges</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="badges" className="space-y-6">
          <TabsList className={`grid w-full ${isCreator ? 'grid-cols-7' : 'grid-cols-6'} bg-white border`}>
            <TabsTrigger value="badges">Badges</TabsTrigger>
            {isCreator && (
              <TabsTrigger value="recordings">
                <Video className="h-4 w-4 mr-2" />
                Recordings
              </TabsTrigger>
            )}
            <TabsTrigger value="followers">Fans</TabsTrigger>
            <TabsTrigger value="following">Following</TabsTrigger>
            <TabsTrigger value="referrals">Referrals</TabsTrigger>
            <TabsTrigger value="activity">Activity</TabsTrigger>
            <TabsTrigger value="settings">
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="badges">
            <Card className="clean-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-primary" />
                  Your Achievements
                </CardTitle>
              </CardHeader>
              <CardContent>
                {badges.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">
                    No badges earned yet. Keep engaging to unlock achievements!
                  </p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {badges.map((userBadge) => (
                      <Card key={userBadge.id} className="text-center clean-card hover-lift">
                        <CardContent className="p-4">
                          <div className="mb-2">
                            <Award className="h-12 w-12 mx-auto text-primary" />
                          </div>
                          <p className="font-semibold text-sm text-foreground">
                            {userBadge.badges?.name}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {userBadge.badges?.description}
                          </p>
                          <p className="text-xs text-muted-foreground mt-2">
                            Earned {new Date(userBadge.earned_at).toLocaleDateString()}
                          </p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* XP & Level System */}
            <Card className="mt-6 clean-card">
              <CardHeader>
                <CardTitle>Level & Experience</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex items-center justify-center h-16 w-16 rounded-full bg-primary text-white">
                    <span className="text-2xl font-bold">5</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium text-foreground">Level 5</span>
                      <span className="text-sm text-muted-foreground">750 / 1000 XP</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full gradient-purple"
                        style={{ width: '75%' }}
                      />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div className="text-center p-3 border rounded-xl bg-white">
                    <Star className="h-5 w-5 mx-auto mb-1 text-primary" />
                    <p className="text-lg font-bold text-foreground">750</p>
                    <p className="text-xs text-muted-foreground">Total XP</p>
                  </div>
                  <div className="text-center p-3 border rounded-xl bg-white">
                    <Trophy className="h-5 w-5 mx-auto mb-1 text-primary" />
                    <p className="text-lg font-bold text-foreground">12</p>
                    <p className="text-xs text-muted-foreground">Achievements</p>
                  </div>
                  <div className="text-center p-3 border rounded-xl bg-white">
                    <Heart className="h-5 w-5 mx-auto mb-1 text-primary" />
                    <p className="text-lg font-bold text-foreground">5</p>
                    <p className="text-xs text-muted-foreground">Rank</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {isCreator && (
            <TabsContent value="recordings">
              <Card className="clean-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Video className="h-5 w-5 text-primary" />
                    Published Recordings
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {recordings.length === 0 ? (
                    <div className="text-center py-8">
                      <Video className="h-12 w-12 mx-auto text-muted-foreground/50 mb-2" />
                      <p className="text-muted-foreground">No published recordings yet</p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Enable "Save and publish recording" when going live to post recordings here
                      </p>
                    </div>
                  ) : (
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {recordings.map((recording) => (
                        <Card
                          key={recording.id}
                          className="clean-card hover-lift cursor-pointer group"
                          onClick={() => navigate(`/watch/${recording.id}`)}
                        >
                          <div className="relative aspect-video overflow-hidden rounded-t-lg">
                            {recording.thumbnail_url ? (
                              <img
                                src={recording.thumbnail_url}
                                alt={recording.title}
                                className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-300"
                              />
                            ) : (
                              <div className="flex items-center justify-center w-full h-full bg-muted">
                                <Video className="h-8 w-8 text-muted-foreground" />
                              </div>
                            )}
                            <div className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-2 py-0.5 rounded">
                              {Math.floor(recording.duration / 60)}:{(recording.duration % 60).toString().padStart(2, '0')}
                            </div>
                          </div>
                          <CardContent className="p-3">
                            <h3 className="font-semibold text-sm text-foreground line-clamp-2 mb-1">
                              {recording.title}
                            </h3>
                            <p className="text-xs text-muted-foreground">
                              {recording.view_count || 0} views • {new Date(recording.published_at).toLocaleDateString()}
                            </p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          <TabsContent value="followers" className="space-y-6">
            {user && <FollowersList userId={user.id} />}
            <SuggestedCreators />
          </TabsContent>

          <TabsContent value="following">
            {user && <FollowingList userId={user.id} />}
          </TabsContent>

          <TabsContent value="referrals">
            <ReferralDashboard />
          </TabsContent>

          <TabsContent value="activity">
            <Card className="clean-card">
              <CardHeader>
                <CardTitle>Recent Activity</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-center py-8 text-muted-foreground">
                  No recent activity
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings" className="space-y-6">
            <NotificationPreferences />
            <DataExport />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Profile;
